import { formatLocalizedNumber } from 'lib-analytics-ui';
import { Timer } from 'lib-shared-legacy-components';
import { Media } from 'lib-shared-types-channels';
import moment from 'moment-timezone';
import { memo, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import {
  TvDashboardMetric,
  TvDashboardMetricItem,
} from 'app/views/tvDashboard/components/metric';
import { useDashboardRefetchEffect } from 'utils/dashboard';

import styles from './tvChannelDashboardMetrics.module.scss';

type TvChannelDashboardMetricsProps = {
  /** The channel media type. */
  media: Media;
};

export const TvChannelDashboardMetrics = memo(
  function TvChannelDashboardMetrics({
    media,
  }: TvChannelDashboardMetricsProps): JSX.Element {
    const { metrics, slaPolicy, isLoading, refetch } =
      dashboardApi.useReadChannelDashboardMetricsQuery(undefined, {
        // Automatically refetch the dashboard data via an HTTP request every hour to ensure the
        // access/refresh tokens are refreshed and the TV dashboard remains logged in indefinitely.
        pollingInterval: 1000 * 60 * 60,
        selectFromResult: ({ data, ...rest }) => {
          const { metrics, sla_policies } =
            data?.data.channel_metrics.find(({ id }) => id === media) ?? {};

          return {
            ...rest,
            metrics,
            slaPolicy: sla_policies?.[0],
          };
        },
      });

    useDashboardRefetchEffect({ refetch });

    const metricItems = useMemo((): readonly TvDashboardMetricItem[] => {
      const inQueue = metrics?.conversations_in_queue ?? 0;
      const open = metrics?.conversations_open ?? 0;
      const total = inQueue + open;
      const availableAgents = metrics?.agents_available ?? 0;
      const onlineAgents = metrics?.agents_online ?? 0;
      const slaResponseTimeMetric = slaPolicy?.metrics.find(
        ({ metric }) => metric === 'response_time',
      );
      const slaPercentage = slaResponseTimeMetric?.achieved_percentage ?? 0;

      return [
        {
          title: 'Waiting',
          stat: formatLocalizedNumber(inQueue),
          color: 'orange',
        },
        {
          title: 'Total pending',
          stat: formatLocalizedNumber(total),
        },
        {
          title: 'Assigned',
          stat: formatLocalizedNumber(open),
          color: 'purple',
        },
        {
          title: 'Longest queued time',
          stat: metrics?.longest_queued_time ? (
            <Timer
              asCounter={false}
              start
              startAt={moment(metrics.longest_queued_time).toISOString()}
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
          color: 'green',
        },
        {
          title: 'Answered',
          stat: formatLocalizedNumber(metrics?.conversations_answered ?? 0),
          color: 'green',
        },
        {
          title: 'Received',
          stat: formatLocalizedNumber(metrics?.conversations_received ?? 0),
        },
        {
          title: 'Avg. assignment time',
          stat: metrics?.average_assignment_time ? (
            <Timer
              asCounter
              start={false}
              startAt={metrics.average_assignment_time / 1000}
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
        },
        {
          title: 'Available agents',
          stat: `${availableAgents} / ${onlineAgents}`,
          color: 'green',
        },
        {
          title: 'Abandoned',
          stat:
            typeof metrics?.conversations_abandoned === 'number'
              ? formatLocalizedNumber(metrics.conversations_abandoned)
              : '-',
          color: 'red',
        },
        {
          title: 'SLA',
          stat: slaPercentage ? `${slaPercentage.toFixed(1)}%` : '-',
          color:
            slaResponseTimeMetric?.evaluation_status === 'Breached'
              ? 'red'
              : 'green',
        },
        {
          title: 'Avg. response time',
          stat: metrics?.average_response_time ? (
            <Timer
              asCounter
              start={false}
              startAt={metrics.average_response_time / 1000}
              textFitProps={{ max: 70 }}
            />
          ) : (
            '00:00:00'
          ),
          color: 'green',
        },
      ];
    }, [metrics, slaPolicy]);

    return (
      <div className={styles.grid}>
        {metricItems.map((metric, index) => (
          <TvDashboardMetric
            key={index}
            className={styles.item}
            metric={metric}
            isLoading={isLoading}
          />
        ))}
      </div>
    );
  },
);
